/**
 * 轻量中英文切换（兼容 Vue 2.5，不依赖 vue-i18n）
 * 仅 /sale 模块启用英文；其他模块固定中文，不受语言偏好影响
 */
import Vue from 'vue'
import ElementLocale from 'element-ui/lib/locale'
import zhLocale from 'element-ui/lib/locale/lang/zh-CN'
import enLocale from 'element-ui/lib/locale/lang/en'
import messages from './messages'

var STORAGE_KEY = 'app_lang'

/**
 * 是否销售模块路径（仅此模块支持英文）
 * @param {string} path
 * @returns {boolean}
 */
function isSalePath(path) {
  return typeof path === 'string' && path.indexOf('/sale') === 0
}

/**
 * Element 英文分页/空数据文案（对齐订单记录对照表）
 */
var enLocaleCustom = Object.assign({}, enLocale, {
  el: Object.assign({}, enLocale.el, {
    pagination: Object.assign({}, enLocale.el.pagination, {
      total: '{total} Items in Total',
      pagesize: ' Items/Page',
      goto: 'Go to',
      pageClassifier: ' Page'
    }),
    table: Object.assign({}, enLocale.el.table, {
      emptyText: 'No Data Available'
    })
  })
})

/**
 * 渠道名常见词片段替换（无完整对照时的兜底）
 */
var CHANNEL_WORD_MAP = [
  ['用户自助购买', 'Self-Service Purchase'],
  ['默认渠道', 'Default Channel'],
  ['测试渠道', 'Test Channel'],
  ['默认', 'Default'],
  ['测试', 'Test'],
  ['线上', 'Online'],
  ['线下', 'Offline'],
  ['门店', 'Store'],
  ['官网', 'Official Site'],
  ['微信', 'WeChat'],
  ['支付宝', 'Alipay'],
  ['小程序', 'Mini Program'],
  ['电话', 'Phone'],
  ['代理', 'Agent'],
  ['分销', 'Distribution'],
  ['内部', 'Internal'],
  ['渠道', ' Channel']
]

/**
 * @param {string} locale
 * @param {string} key
 * @param {Object} [params]
 * @returns {string}
 */
function translate(locale, key, params) {
  var dict = messages[locale] || messages.zh
  var text = dict[key] || messages.zh[key] || key
  if (params) {
    Object.keys(params).forEach(function (k) {
      text = text.replace(new RegExp('\\{' + k + '\\}', 'g'), params[k])
    })
  }
  return text
}

/**
 * 规范化产品介绍页中文名，便于对照翻译
 * @param {string} name
 * @returns {string}
 */
function normalizeIntroName(name) {
  return String(name)
    .replace(/\s+/g, '')
    .replace(/[（(]/g, '(')
    .replace(/[）)]/g, ')')
    .replace(/－/g, '-')
    .replace(/—/g, '-')
    .replace(/–/g, '-')
}

/**
 * 翻译销售渠道名称（接口动态中文名）
 * @param {string} locale
 * @param {string} name
 * @returns {string}
 */
function translateChannel(locale, name) {
  if (name == null || name === '') return ''
  var str = String(name)
  if (locale === 'zh') return str
  var exactKey = 'channel.' + str
  var dict = messages.en || {}
  if (dict[exactKey]) return dict[exactKey]
  var result = str
  for (var i = 0; i < CHANNEL_WORD_MAP.length; i++) {
    var from = CHANNEL_WORD_MAP[i][0]
    var to = CHANNEL_WORD_MAP[i][1]
    if (result.indexOf(from) !== -1) {
      result = result.split(from).join(to)
    }
  }
  return result.replace(/\s+/g, ' ').trim()
}

/**
 * 魅力中国游优选/尊享清单方案 1-3：按关键词写死英文名（保留原名中的天数区间）
 * @param {string} normalized 已规范化中文名
 * @returns {string|null}
 */
function mapCharmChinaPlanName(normalized) {
  if (!normalized || normalized.indexOf('魅力中国游') === -1) return null
  var prefix = ''
  if (normalized.indexOf('优选版') !== -1) {
    prefix = 'Charm of China Tour (Preferred) – JT Travel China Insurance plan '
  } else if (normalized.indexOf('尊享版') !== -1) {
    prefix = 'Charm of China Tour (Premium) – JT Travel China Insurance plan '
  } else {
    return null
  }
  var dayMatch = normalized.match(/(\d+)\s*[-~～到至]\s*(\d+)\s*天/)
  var daySuffix = dayMatch ? (' (' + dayMatch[1] + '-' + dayMatch[2] + ' days)') : ''
  if (/方案(?:1|一)/.test(normalized)) return prefix + '1' + daySuffix
  if (/方案(?:2|二)/.test(normalized)) return prefix + '2' + daySuffix
  if (/方案(?:3|三)/.test(normalized)) return prefix + '3' + daySuffix
  return null
}

/**
 * 在对照表中查找规范化后的中文名
 * @param {Object} dict
 * @param {string} prefix
 * @param {string} normalized
 * @returns {string|null}
 */
function findNamedByNormalized(dict, prefix, normalized) {
  var keys = Object.keys(dict)
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i]
    if (key.indexOf(prefix) !== 0) continue
    var zhName = key.slice(prefix.length)
    if (normalizeIntroName(zhName) === normalized) {
      return dict[key]
    }
  }
  return null
}

/**
 * 按前缀翻译接口动态中文名
 * @param {string} locale
 * @param {string} name
 * @param {string} prefix
 * @returns {string}
 */
function translateNamed(locale, name, prefix) {
  if (name == null || name === '') return ''
  var str = String(name)
  if (locale === 'zh') return str
  var dict = messages.en || {}
  var exactKey = prefix + str
  if (dict[exactKey]) return dict[exactKey]
  var normalized = normalizeIntroName(str)
  var matched = findNamedByNormalized(dict, prefix, normalized)
  if (matched) return matched
  var charmMapped = mapCharmChinaPlanName(normalized)
  if (charmMapped) return charmMapped
  var planMatch = normalized.match(/方案([123一二三])$/)
  if (planMatch) {
    var baseNormalized = normalized.replace(/方案[123一二三]$/, '')
    var baseMapped = findNamedByNormalized(dict, prefix, baseNormalized)
    if (baseMapped) {
      var planNo = planMatch[1]
      if (planNo === '一') planNo = '1'
      if (planNo === '二') planNo = '2'
      if (planNo === '三') planNo = '3'
      return baseMapped + ' plan ' + planNo
    }
  }
  return str
}

/**
 * 翻译产品介绍页产品名、权益名（接口动态中文名）
 * @param {string} locale
 * @param {string} name
 * @returns {string}
 */
function translateIntroName(locale, name) {
  return translateNamed(locale, name, 'intro.name.')
}

/**
 * 翻译商品页产品名（本页对照表优先，没有则回退介绍页译名）
 * @param {string} locale
 * @param {string} name
 * @returns {string}
 */
function translateCommodityName(locale, name) {
  if (name == null || name === '') return ''
  var str = String(name)
  if (locale === 'zh') return str
  var mapped = translateNamed(locale, str, 'commodity.name.')
  if (mapped !== str) return mapped
  return translateIntroName(locale, str)
}

/**
 * 翻译接口返回的中文提示（英文模式下查对照表）
 * @param {string} locale
 * @param {string} msg
 * @param {string|number} [code]
 * @returns {string}
 */
function translateApiMsg(locale, msg, code) {
  if (msg == null || msg === '') return ''
  var str = String(msg).trim()
  if (locale === 'zh') return str
  var dict = messages.en || {}
  if (code != null && code !== '' && dict['api.code.' + code]) {
    return dict['api.code.' + code]
  }
  if (dict['api.msg.' + str]) {
    return dict['api.msg.' + str]
  }
  // 去掉末尾标点再匹配一次
  var normalized = str.replace(/[！!。．.]+$/g, '')
  if (normalized !== str && dict['api.msg.' + normalized]) {
    return dict['api.msg.' + normalized]
  }
  return str
}

export var i18nStore = new Vue({
  data: function () {
    return {
      locale: localStorage.getItem(STORAGE_KEY) || 'en'
    }
  },
  methods: {
    /**
     * 当前路径下实际生效的语言（非 sale 固定中文）
     * @param {string} [path]
     * @returns {string}
     */
    effectiveLocale: function (path) {
      return isSalePath(path) ? this.locale : 'zh'
    },
    /**
     * 按路由同步 Element UI 语言（仅 sale + 偏好英文时用英文）
     * @param {string} [path]
     */
    syncElementByPath: function (path) {
      var useEn = isSalePath(path) && this.locale === 'en'
      ElementLocale.use(useEn ? enLocaleCustom : zhLocale)
      if (typeof document !== 'undefined') {
        document.documentElement.lang = useEn ? 'en' : 'zh-CN'
      }
    },
    /**
     * @param {string} key
     * @param {Object} [params]
     * @param {string} [path] 当前路由，用于限定仅 sale 用英文
     * @returns {string}
     */
    t: function (key, params, path) {
      return translate(this.effectiveLocale(path), key, params)
    },
    /**
     * @param {string} name 渠道中文名
     * @param {string} [path]
     * @returns {string}
     */
    tChannel: function (name, path) {
      return translateChannel(this.effectiveLocale(path), name)
    },
    /**
     * @param {string} name 产品介绍页中文名
     * @param {string} [path]
     * @returns {string}
     */
    tIntroName: function (name, path) {
      return translateIntroName(this.effectiveLocale(path), name)
    },
    /**
     * @param {string} name 商品页产品中文名
     * @param {string} [path]
     * @returns {string}
     */
    tCommodityName: function (name, path) {
      return translateCommodityName(this.effectiveLocale(path), name)
    },
    /**
     * 翻译接口提示文案
     * @param {string} msg
     * @param {string|number} [code]
     * @param {string} [path]
     * @returns {string}
     */
    tApiMsg: function (msg, code, path) {
      return translateApiMsg(this.effectiveLocale(path), msg, code)
    },
    /**
     * @param {string} locale zh | en
     * @param {string} [path] 当前路由，决定是否立即切换 Element 英文
     */
    setLocale: function (locale, path) {
      if (locale !== 'zh' && locale !== 'en') return
      this.locale = locale
      localStorage.setItem(STORAGE_KEY, locale)
      this.syncElementByPath(path)
    }
  }
})

/**
 * 初始化当前语言（含 Element UI）
 * 英文仅作用于 /sale；其他模块始终中文
 * @param {import('vue-router').default} [router]
 */
export function initI18n(router) {
  // 默认 Element 中文，避免非 sale 页被英文污染
  ElementLocale.use(zhLocale)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = 'zh-CN'
  }
  if (router && typeof router.afterEach === 'function') {
    router.afterEach(function (to) {
      i18nStore.syncElementByPath(to && to.path)
    })
    if (router.currentRoute && router.currentRoute.path) {
      i18nStore.syncElementByPath(router.currentRoute.path)
    }
  }
  Vue.mixin({
    computed: {
      /**
       * 当前语言偏好（sale 顶栏切换用）；非 sale 页面翻译仍固定中文
       * @returns {string}
       */
      appLocale: function () {
        return i18nStore.locale
      }
    },
    methods: {
      /**
       * 当前是否销售模块（英文仅此处生效）
       * @returns {boolean}
       */
      isSaleI18nRoute: function () {
        return !!(this.$route && isSalePath(this.$route.path))
      },
      /**
       * @param {string} key
       * @param {Object} [params]
       * @returns {string}
       */
      $t: function (key, params) {
        void this.appLocale
        var path = this.$route && this.$route.path
        return i18nStore.t(key, params, path)
      },
      /**
       * 销售渠道名称翻译
       * @param {string} name
       * @returns {string}
       */
      $tChannel: function (name) {
        void this.appLocale
        var path = this.$route && this.$route.path
        return i18nStore.tChannel(name, path)
      },
      /**
       * 产品介绍页产品名、权益名翻译
       * @param {string} name
       * @returns {string}
       */
      $tIntroName: function (name) {
        void this.appLocale
        var path = this.$route && this.$route.path
        return i18nStore.tIntroName(name, path)
      },
      /**
       * 商品页产品名翻译
       * @param {string} name
       * @returns {string}
       */
      $tCommodityName: function (name) {
        void this.appLocale
        var path = this.$route && this.$route.path
        return i18nStore.tCommodityName(name, path)
      },
      /**
       * 接口提示文案翻译
       * @param {string} msg
       * @param {string|number} [code]
       * @returns {string}
       */
      $tApiMsg: function (msg, code) {
        void this.appLocale
        var path = this.$route && this.$route.path
        return i18nStore.tApiMsg(msg, code, path)
      },
      /**
       * @param {string} locale
       */
      $setLocale: function (locale) {
        var path = this.$route && this.$route.path
        i18nStore.setLocale(locale, path)
      }
    }
  })
}

export default i18nStore



// WEBPACK FOOTER //
// ./src/i18n/index.js